import type {ReactElement} from 'react';
import React from 'react';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import {SlideshowList} from '@Components/contextual-menu/components/slideshow-list';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {TextSlideItem} from '@Components/contextual-menu/components/text-slide-item';
import {ImageSlideItem} from '@Components/contextual-menu/components/image-slide-item';
import {VideoSlideItem} from '@Components/contextual-menu/components/video-slide-item';
import {repoVideoThumbImageURL} from '@Libraries/user-video-library';
import {MIN_SLIDE_DURATION} from '@PosterWhiteboard/items/slideshow-item/slideshow-slides.class';
import type {SlideshowDuration} from '@Components/contextual-menu/components/slideshow-list-item/slideshow-list-item.types';

export function SlideshowListOption(): ReactElement<any> {
  const activeItem = useSingleActivePosterItem();

  if (!activeItem) {
    throw new Error('Active item does not exist in SlideshowSlidesOption');
  }

  if (!activeItem.isSlideshow()) {
    throw new Error(`Expected active item to be of type slideshow, instead got ${activeItem.gitype}`);
  }

  const {slidesOrder, slidesHashMap} = activeItem.slides;

  const updateSlideText = async (text: string, id: string, undoable = true): Promise<void> => {
    const slideItem = slidesHashMap[id];
    if (!slideItem.isTextSlide()) {
      throw new Error(`Expected TextSlide, instead got ${slidesHashMap[id].gitype}`);
    }

    void slideItem.slideshow.updateSlideFromObject(
      id,
      {
        text,
        textStyles: undoable ? slideItem.textStyles.getLanguageScriptAndFontForText(text) : {},
      },
      {
        undoable,
      }
    );

    if (undoable) {
      await slideItem.slideshow.page.spellCheck.checkSpellForSelectedObject();
    }
  };

  const updateSlideDuration = (slideDuration: number, id: string, undoable = true): void => {
    const slideItem = slidesHashMap[id];

    void slideItem.slideshow.updateSlideFromObject(
      id,
      {
        slideDuration,
      },
      {
        undoable,
        checkForDurationUpdate: true,
      }
    );
  };

  const onChangeText = (text: string, id: string): void => {
    void updateSlideText(text, id, false);
  };

  const onChangeTextEnded = (text: string, id: string): void => {
    void updateSlideText(text, id);
  };

  const onDelete = (id: string): void => {
    activeItem.deleteSlide(id);
  };

  const onClick = (id: string): void => {
    activeItem.slides.seekToSlideId(id);
  };

  const onChangeDuration = (val: number, id: string): void => {
    updateSlideDuration(val, id, false);
  };
  const onChangeDurationEnded = (val: number, id: string): void => {
    if (val <= MIN_SLIDE_DURATION) {
      updateSlideDuration(MIN_SLIDE_DURATION, id);
      return;
    }
    const slideMaxDuration = activeItem.slides.getMaxValidDuration(id);
    if (slideMaxDuration >= val) {
      updateSlideDuration(Number(val.toFixed(1)), id);
      return;
    }
    updateSlideDuration(slideMaxDuration, id);
  };

  const onDragEnd = (sourceIndex: number, destinationIndex: number): void => {
    activeItem.slides.moveSlideToIndex(slidesOrder[sourceIndex], destinationIndex);
  };

  const showDuration = (): boolean => {
    return !(slidesOrder.length === 1 && activeItem.onPageStartTime === 0);
  };

  const getDurationObject = (duration: number): SlideshowDuration => {
    return {
      showDuration: showDuration(),
      duration,
      onChangeDuration,
      onChangeDurationEnded,
    };
  };

  const prepareTextSlide = (id: string, index: number): ReactElement<any> => {
    const slideItem = slidesHashMap[id];
    if (!slideItem.isTextSlide()) {
      throw new Error(`Expected TextSlide, instead got ${slidesHashMap[id].gitype}`);
    }
    const isSlideSelected = activeItem.getSelectedSlide().uid === id;
    return (
      <TextSlideItem
        id={slideItem.uid}
        key={slideItem.uid}
        index={index}
        input={slideItem.text}
        onInputChange={onChangeText}
        onInputChangeEnded={onChangeTextEnded}
        onDelete={onDelete}
        onClick={onClick}
        isSelected={isSlideSelected}
        duration={getDurationObject(slideItem.slideDuration)}
      />
    );
  };

  const prepareImageSlide = (id: string, index: number): ReactElement<any> => {
    const slideItem = slidesHashMap[id];
    if (!slideItem.isImageSlide()) {
      throw new Error(`Expected imageSlide, instead got ${slidesHashMap[id].gitype}`);
    }

    return (
      <ImageSlideItem
        getImgSrc={(): Promise<string> => {
          return slideItem.getImageUrl();
        }}
        id={slideItem.uid}
        key={slideItem.uid}
        index={index}
        onDelete={onDelete}
        onClick={onClick}
        isSelected={activeItem.getSelectedSlide().uid === id}
        duration={getDurationObject(slideItem.slideDuration)}
      />
    );
  };
  const prepareVideoSlide = (id: string, index: number): ReactElement<any> => {
    const slideItem = slidesHashMap[id];
    if (!slideItem.isVideoSlide()) {
      throw new Error(`Expected videoSlide, instead got ${slidesHashMap[id].gitype}`);
    }

    return (
      <VideoSlideItem
        videoSrc={repoVideoThumbImageURL(slideItem.hashedFilename, slideItem.fileExtension)}
        id={slideItem.uid}
        key={slideItem.uid}
        index={index}
        onDelete={onDelete}
        onClick={onClick}
        isSelected={activeItem.getSelectedSlide().uid === id}
        duration={getDurationObject(slideItem.slideDuration)}
      />
    );
  };

  const prepareSlideShowListItem = (id: string, index: number): ReactElement<any> => {
    switch (slidesHashMap[id].gitype) {
      case ITEM_TYPE.TEXTSLIDE:
        return prepareTextSlide(id, index);
      case ITEM_TYPE.IMAGESLIDE:
        return prepareImageSlide(id, index);
      case ITEM_TYPE.VIDEOSLIDE:
        return prepareVideoSlide(id, index);
      default:
        throw new Error('unhandled slide type in slideshow hashmap');
    }
  };

  return <SlideshowList renderItem={prepareSlideShowListItem} slidesOrder={slidesOrder} onDragEnd={onDragEnd} id={activeItem.uid} />;
}
